import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { KeyRound, Trash2, ArrowLeft, LogOut, Save, ShieldAlert, CheckCircle2 } from "lucide-react";

/**
 * /account — self-service settings.
 * - Change password (any logged-in user)
 * - Delete account (App Store / Play Store required)
 * - Log out
 */
export default function AccountSettings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Change password form
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");

  // Delete account form
  const [delPw, setDelPw] = useState("");
  const [delConfirm, setDelConfirm] = useState("");

  const flash = (kind, text) => {
    if (kind === "msg") setMsg(text); else setErr(text);
    setTimeout(() => { setMsg(""); setErr(""); }, 6000);
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (n1 !== n2) { flash("err", "New passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: n1 });
      setCur(""); setN1(""); setN2("");
      flash("msg", "Password changed successfully.");
    } catch (e2) { flash("err", e2.response?.data?.detail || "Change failed"); }
    finally { setBusy(false); }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (delConfirm.trim().toUpperCase() !== "DELETE") { flash("err", 'Please type DELETE exactly.'); return; }
    if (!window.confirm("This will PERMANENTLY delete your account. This cannot be undone. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password: delPw, confirm_text: delConfirm });
      // Fully log the user out client-side and route home
      try { await logout(); } catch {}
      alert("Your account has been permanently deleted.");
      nav("/", { replace: true });
    } catch (e2) { flash("err", e2.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="account-settings-page">
      <header className="bg-white border-b-2 border-asap-deep">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-asap-deep hover:text-asap-orange" data-testid="account-back">
            <ArrowLeft size={14}/> Back
          </button>
          <Logo size={48} />
          <button onClick={async () => { await logout(); nav("/"); }} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-asap-red" data-testid="account-logout">
            <LogOut size={12}/> Logout
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase text-asap-deep">Account Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Signed in as <span className="font-mono">{user.email}</span></p>
        </div>

        {msg && <div className="border-2 border-emerald-500 bg-emerald-50 text-emerald-800 px-4 py-2 font-bold text-sm flex items-center gap-2"><CheckCircle2 size={16}/> {msg}</div>}
        {err && <div className="border-2 border-asap-red bg-red-50 text-asap-red px-4 py-2 font-bold text-sm flex items-center gap-2"><ShieldAlert size={16}/> {err}</div>}

        {/* Change Password */}
        <section className="bg-white border-2 border-asap-deep p-5" data-testid="change-pw-section">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound size={18} className="text-asap-orange"/>
            <h2 className="font-display uppercase text-asap-deep">Change Password</h2>
          </div>
          <form onSubmit={changePw} className="space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Current password</div>
              <input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="change-pw-current" className="w-full px-3 py-2 border-2 border-asap-deep text-sm" autoComplete="current-password"/>
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">New password (min 6)</div>
                <input type="password" required minLength={6} value={n1} onChange={(e) => setN1(e.target.value)} data-testid="change-pw-new" className="w-full px-3 py-2 border-2 border-asap-deep text-sm" autoComplete="new-password"/>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Confirm new password</div>
                <input type="password" required minLength={6} value={n2} onChange={(e) => setN2(e.target.value)} data-testid="change-pw-confirm" className="w-full px-3 py-2 border-2 border-asap-deep text-sm" autoComplete="new-password"/>
              </label>
            </div>
            <button type="submit" disabled={busy} data-testid="change-pw-submit" className="px-5 py-2 bg-asap-yellow text-asap-deep font-bold text-xs uppercase tracking-wider border-2 border-asap-deep inline-flex items-center gap-1 disabled:opacity-50">
              <Save size={13}/> Save New Password
            </button>
          </form>
        </section>

        {/* Danger zone — Delete account */}
        <section className="bg-red-50 border-2 border-asap-red p-5" data-testid="delete-account-section">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 size={18} className="text-asap-red"/>
            <h2 className="font-display uppercase text-asap-red">Delete My Account</h2>
          </div>
          <div className="text-sm text-slate-700 space-y-1 mb-3">
            <p>This permanently deletes your login, profile, saved documents, phone number, and message history. Booking records are kept anonymized for financial records.</p>
            <p><strong>You can't delete while you have active bookings.</strong> Cancel or complete them first.</p>
          </div>
          <form onSubmit={deleteAccount} className="space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-asap-red mb-1">Confirm your password</div>
              <input type="password" required value={delPw} onChange={(e) => setDelPw(e.target.value)} data-testid="delete-pw" className="w-full px-3 py-2 border-2 border-asap-red text-sm bg-white" autoComplete="current-password"/>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-asap-red mb-1">Type <span className="font-mono">DELETE</span> to confirm</div>
              <input type="text" required value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} data-testid="delete-confirm" placeholder="DELETE" className="w-full px-3 py-2 border-2 border-asap-red text-sm bg-white font-mono"/>
            </label>
            <button type="submit" disabled={busy} data-testid="delete-account-submit" className="px-5 py-2 bg-asap-red text-white font-bold text-xs uppercase tracking-wider border-2 border-asap-deep inline-flex items-center gap-1 disabled:opacity-50">
              <Trash2 size={13}/> Permanently Delete My Account
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
